/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import React, {ReactElement, useContext, useEffect, useMemo, useRef} from 'react';
import {Redirect, Route, useLocation, useParams} from 'react-router-dom';
import Account from '../../../pages/workflows/account/Account';
import {useRoutingUtils} from '../../RoutingUtils';
import {useChartOfAccounts} from '../../../contexts/chartofaccounts/ChartOfAccountsContext';
import AverageRent from '../../../pages/workflows/operational/average-rent/AverageRent';
import EarlyTerminations from '../../../pages/workflows/operational/early-terminations/EarlyTerminations';
import LeaseExpirations from '../../../pages/workflows/operational/lease-expiration/LeaseExpiration';
import MonthToMonthMoveOuts from '../../../pages/workflows/operational/month-to-month-move-outs/MonthToMonthMoveOuts';
import MoveOutRates from '../../../pages/workflows/operational/move-outs/MoveOutRates';
import OccupancyRates from '../../../pages/workflows/operational/occupancy-rates/OccupancyRates';
import RenewalRates from '../../../pages/workflows/operational/renewal-rates/RenewalRates';
import RenewalRatios from '../../../pages/workflows/operational/renewal-ratios/RenewalRatios';
import Error from '../../../components/error/Error';
import {REVENUE_SOURCE_DESCRIPTIONS, RevenueSource} from '../../../constants/RevenueWorkflow';
import useAppStore from '../../../hooks/useAppStore';
import Skeleton from '../../../components/dashboard';
import "native-injects";
import AccountRenovations from "../../../pages/workflows/renovations/AccountRenovations";
import AccountPayroll from "../../../pages/workflows/payroll/AccountPayroll";
import {getBudgetYearForProperty, useProperties} from "../../../contexts/properties/PropertiesContext";
import {AuthorizationContext} from "../../../authorization/AuthorizationContext";
import {Authorizations} from "../../../authorization/Authorizations";
import ReadOnlyHeader from "../../../components/navbar/ReadOnlyHeader";
import {RenderIfReadOnly} from "../../../authorization/RenderIfAuthorized";

import * as navStyles from "../../../components/navbar/styles.module.scss";
import {useRenovationsWorkflow} from "../../../hooks/useRenovationsWorkflow";
import {IWorkflowPageProps} from "../../../pages/workflows/logic/workflows";
import LeaseExpirationSchedule from "../../../pages/simplified-revenue/LeaseExpirationSchedule";
import MarketRent from "../../../pages/simplified-revenue/MarketRent";
import Summary from "../../../pages/simplified-revenue/Summary";
import {buildPrevNext, buildTreeNodes} from "./AccountRouteConstants";
import RenewalTradeOut from "../../../pages/simplified-revenue/RenewalTradeOut";
import RenewalRatio from "../../../pages/simplified-revenue/RenewalRatio";
import Renovations from '../../../pages/simplified-revenue/Renovations';
import VacancyRate from '../../../pages/simplified-revenue/VacancyRate';
import {RevenueSummaryV2} from "../../../pages/workflows/operational/summary/RevenueSummaryV2";
import {AuthContext} from "../../../contexts/AuthContext";
import {
    AffordableInPlaceRentChange
} from "../../../pages/workflows/operational/affordable/in-place-rent-change/AffordableInPlaceRentChange";
import {AffordableGeneric} from "../../../pages/workflows/operational/affordable/generic/AffordableGeneric";
import {
    AffordableRevenueSummary
} from "../../../pages/workflows/operational/affordable/summary/AffordableRevenueSummary";
import {TreeNode} from "../../../components/tree/Tree";
import {AdminModelDown} from "../../../pages/workflows/operational/admin-model-down/AdminModelDown";
import OccupancyManager from '../../../pages/workflows/operational/occupancy-manager/OccupancyManager';
import {LossLeaders} from "../../../pages/workflows/operational/loss-leaders/LossLeaders";
import UserSpecifiedUnitCount
    from "../../../pages/workflows/operational/user-specified-unit-count/UserSpecifiedUnitCount";


const OPERATIONAL_COMPONENTS = {
    [RevenueSource.AVG_MARKET_RENTS]: AverageRent,
    [RevenueSource.EXPIRATIONS]: LeaseExpirations,
    [RevenueSource.RENEWALS_RATIO]: RenewalRatios,
    [RevenueSource.RENEWALS_RATE]: RenewalRates,
    [RevenueSource.MOVE_OUTS]: MoveOutRates,
    [RevenueSource.USER_SPECIFIED_UNIT_COUNT]: UserSpecifiedUnitCount,
    [RevenueSource.EARLY_TERMS]: EarlyTerminations,
    [RevenueSource.MONTH_TO_MONTH]: MonthToMonthMoveOuts,
    [RevenueSource.OCCUPANCY]: OccupancyRates,
    [RevenueSource.SUMMARY]: RevenueSummaryV2,
    [RevenueSource.OCCUPANCY_MANAGER]: OccupancyManager,
    [RevenueSource.ADMIN_MODEL_DOWN]: AdminModelDown,
    [RevenueSource.LOSS_LEADERS]: LossLeaders,
};

const PAGE_TO_KEY = Object.entries(REVENUE_SOURCE_DESCRIPTIONS)
    .toIdMap(([_, row]) => row.path, ([key]) => key as RevenueSource);


interface OperationalProps extends IWorkflowPageProps{
    page: string;
}

function OperationalRoute(props: OperationalProps): ReactElement {
    const key = PAGE_TO_KEY[props.page];
    const Component = key ? OPERATIONAL_COMPONENTS[key] : undefined;

    const memoizedComponent = useMemo(() => {
        if(Component){
            return <Component onNextClick={props.onNextClick} onPreviousClick={props.onPreviousClick} key={Component?.name}/>;
        }
    }, []);

    if(!Component || !memoizedComponent){
        return <Error message="Page not found"/>;
    }


    return memoizedComponent;
}

export function AccountRoute(): ReactElement {
    const appStore = useAppStore();

    const {
        id: currentAccountId,
        page: routeParamPage,
        type: routeParamType
    } = useParams() as {
        id: string;
        page: string;
        type: string | undefined
    };
    const {pathname} = useLocation();

    const categoryType = routeParamType || appStore.categoryType || "reforecast";

    const {hasAuthorization} = useContext(AuthorizationContext);
    const {chartOfAccounts, accounts, getPrevAccountId, getNextAccountId, isReady: isCoaReady} = useChartOfAccounts();
    const {goto} = useRoutingUtils();
    const auth = useContext(AuthContext);

    const {currentProperty} = useProperties();
    const budgetYear = getBudgetYearForProperty(currentProperty, auth.budgetYear);
    const {renovationsWorkflow, renovationsWorkflowDataLoading} = useRenovationsWorkflow({categoryType, budgetYear: budgetYear});

    const {prevMap, nextMap, flat} = useMemo(() => {
        // You MUST ensure that renovationsWorkflow has it's values before returning a "flat" value of any length.
        // If you don't, you'll be immediately redirected away from any renovation page.
        if(!currentProperty || !chartOfAccounts || renovationsWorkflowDataLoading) {
            return {prevMap: new Map<string, TreeNode>(), nextMap: new Map<string, TreeNode>(), flat: []};
        }

        const hasPayroll = hasAuthorization(Authorizations.PlanningHubPayroll);
        const treeNodes = buildTreeNodes(currentProperty, chartOfAccounts, hasPayroll, renovationsWorkflow, auth.user);
        const {prev, next, flat} = buildPrevNext(treeNodes, ["reforecast", "budget"]);
        return {prevMap: prev, nextMap: next, flat: flat};
    }, [currentProperty, chartOfAccounts, renovationsWorkflow, renovationsWorkflowDataLoading]);

    useEffect(() => {
        appStore.set({isLoading: false});
        return () => {
        };
    }, []);

    useEffect(() => {
        if(currentProperty && currentProperty.isAcquisitionProperty && routeParamType === "budget") {
            const path = pathname.replace("/budget", "/reforecast");
            goto(path)();
        }
    }, [currentProperty, routeParamType]);

    useEffect(() => {
        if(!currentProperty || flat.length === 0) {
            return;
        }

        const gotoFirst = !prevMap.has(pathname) && !nextMap.has(pathname);
        if(gotoFirst) {
            const first = flat[0];
            if(first && first.url) {
                let url = first.url;
                if(!first.useRawUrl) {
                    url = `${url}/${categoryType}`;
                }
                return goto(url)();
            }
        }
    }, [currentProperty, pathname, flat, prevMap, nextMap]);


    function goBackRequested() {
        const prev = prevMap.get(pathname);
        if(prev && prev.url) {
            let url = prev.url;
            if(!prev.useRawUrl) {
                url = `${url}/${categoryType}`;
            }
            return goto(url)();
        }
    }

    function goNextRequested() {
        const next = nextMap.get(pathname);
        if(next && next.url) {
            let url = next.url;
            if(!next.useRawUrl) {
                url = `${url}/${categoryType}`;
            }
            return goto(url)();
        }
    }

    // Because the operational routes are so f'd up, I have to use refs to ensure
    // that the next/prev functions actually work for them.
    const goNextRequestedRef = useRef(goNextRequested);
    const goBackRequestedRef = useRef(goBackRequested);
    goNextRequestedRef.current = goNextRequested;
    goBackRequestedRef.current = goBackRequested;

    function opGoNextRequested() {
        if(goNextRequestedRef.current) {
            goNextRequestedRef.current();
        }
    }

    function opGoBackRequested() {
        if(goBackRequestedRef.current) {
            goBackRequestedRef.current();
        }
    }

    // NOTE: This was used as a hack to keep the pages from re-rendering
    // when the goNextRequested/goBackRequested functions were changed :/.
    const memoizedOperationalRoute = useMemo(() => () => (
            <OperationalRoute
                    onNextClick={opGoNextRequested}
                    onPreviousClick={opGoBackRequested}
                    key="operational-route"
                    page={routeParamPage}/>
    ), [routeParamPage]);

    return (
        <Skeleton
            accounts={accounts}
            renovationsWorkflow={renovationsWorkflow}
            isPayrollAvailableForCurrentUser={hasAuthorization(Authorizations.PlanningHubPayroll)}
            user={auth.user}
            onNextClick={goNextRequested}
            onPreviousClick={goBackRequested}
            hideFooter={true}
        >
            <RenderIfReadOnly>
                <ReadOnlyHeader className={navStyles.readOnlyHeaderFixed}/>
            </RenderIfReadOnly>
            <Route exact path="/operational/:page/:type" component={memoizedOperationalRoute}/>
            <Route exact path="/renovations/:unitTypeId/:type">
                <AccountRenovations onNextClick={goNextRequested} onPreviousClick={goBackRequested} budgetYear={budgetYear} key="renos-node"/>
            </Route>
            <Route exact path="/account/:id/:type">
                <Account
                    onNextClick={goNextRequested}
                    onPreviousClick={goBackRequested}
                    accountId={currentAccountId}
                    type={routeParamType as string}
                    key="account-node"
                    budgetYear={budgetYear}
                />
            </Route>
            <Route exact path={"/payroll"}>
                <AccountPayroll onNextClick={goNextRequested} onPreviousClick={goBackRequested} key="payroll-node" />
            </Route>
            <Route path={"/payroll/:type"}>
                <Redirect to={"/payroll"} />
            </Route>

            {/* BEGIN: Routes for Simplified Revenue */}
            {/* You MUST put the route in the SIMPLIFIED_REVENUE_ROUTES list in AccountRouteConstants.ts if you want previous/next to work correctly. */}
            <Route exact path="/simplified-revenue/lease-expiration-schedule">
                <LeaseExpirationSchedule onNextClick={goNextRequested} onPreviousClick={goBackRequested} />
            </Route>
            <Route exact path="/simplified-revenue/market-rent">
                <MarketRent onNextClick={goNextRequested} onPreviousClick={goBackRequested} />
            </Route>
            <Route exact path="/simplified-revenue/summary">
                <Summary onNextClick={goNextRequested} onPreviousClick={goBackRequested} />
            </Route>
            <Route exact path="/simplified-revenue/renewal-trade-out">
                <RenewalTradeOut onNextClick={goNextRequested} onPreviousClick={goBackRequested} />
            </Route>
            <Route exact path="/simplified-revenue/renewal-ratios">
                <RenewalRatio onNextClick={goNextRequested} onPreviousClick={goBackRequested} />
            </Route>
            <Route exact path="/simplified-revenue/renovations">
                <Renovations onNextClick={goNextRequested} onPreviousClick={goBackRequested} />
            </Route>
            <Route exact path="/simplified-revenue/vacancy-rates">
                <VacancyRate onNextClick={goNextRequested} onPreviousClick={goBackRequested} />
            </Route>
            {/* END: Routes for Simplified Revenue */}
            {/* BEGIN: Affordable Revenue */}
            <Route path="/affordable/ipr-change/:type">
                <AffordableInPlaceRentChange onPreviousClick={goBackRequested} onNextClick={goNextRequested} />
            </Route>
            <Route path="/affordable/conventional/move-in/:type">
                <AffordableGeneric onPreviousClick={goBackRequested} onNextClick={goNextRequested} budgetYear={budgetYear} field="moveInCount" format="count" pageName="Conventional Move-Ins" />
            </Route>
            <Route path="/affordable/move-in/:type">
                <AffordableGeneric onPreviousClick={goBackRequested} onNextClick={goNextRequested} budgetYear={budgetYear} field="affordableModelSubsidizedMoveInCount" format="count" pageName="Affordable Move-Ins" />
            </Route>
            <Route path="/affordable/move-out/:type">
                <AffordableGeneric onPreviousClick={goBackRequested} onNextClick={goNextRequested} budgetYear={budgetYear} field="affordableModelSubsidizedMoveOutCount" format="count" pageName="Affordable Move-Outs" />
            </Route>
            <Route path="/affordable/max-rent/:type">
                <AffordableGeneric onPreviousClick={goBackRequested} onNextClick={goNextRequested} budgetYear={budgetYear} field="affordableModelAffordableUnitAvgMaxRent" format="currency" pageName="Max Rents" />
            </Route>
            <Route path="/affordable/summary">
                <AffordableRevenueSummary onNextClick={goNextRequested} onPreviousClick={goBackRequested} />
            </Route>
            {/* END: Affordable Revenue */}
        </Skeleton>
    );
}
